import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Cow,
  PlusCircle,
  ArrowRight,
  FileText,
  VideoCamera,
  Sparkle,
  ClockCounterClockwise,
  TrendUp,
} from "@phosphor-icons/react";
import { api, formatBRL, formatNum, formatDate, downloadMonthlyReport, initials, paletteFor, toastWithQuota } from "@/lib/gado";
import { useAuth } from "@/context/AuthContext";
import { TransactionDetailsDialog } from "@/components/TransactionDetailsDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const MONTHS_FULL = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

// ---------- Small building blocks ----------

function StatChip({ label, value, sub, className = "" }) {
  return (
    <div className={`flex flex-col items-start ${className}`}>
      <span className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-medium">
        {label}
      </span>
      <span className="font-cabinet text-xl md:text-2xl font-extrabold text-white tabular-nums leading-tight">
        {value}
      </span>
      {sub && <span className="text-[11px] text-white/50 mt-0.5">{sub}</span>}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="flex flex-col items-center gap-0.5 px-1 min-w-0">
      <span className="text-[9px] uppercase tracking-[0.14em] text-gado-muted font-semibold">
        {label}
      </span>
      <span className="font-mono text-[13px] font-bold text-gado-ink tabular-nums truncate max-w-full">
        {value}
      </span>
    </div>
  );
}

function RecentRow({ tx, onOpen }) {
  const name = tx.buyer_name || "Sem comprador";
  const meta = [tx.animal_type, tx.iron_brand && `Ferro ${tx.iron_brand}`, tx.city_district]
    .filter(Boolean)
    .join(" · ");
  return (
    <button
      onClick={onOpen}
      data-testid={`recent-tx-${tx.transaction_id}`}
      className="w-full min-w-0 text-left rounded-xl border border-gado-line bg-white p-3.5 hover:border-gado-green/40 hover:shadow-md hover:shadow-gado-green/5 transition-all focus:outline-none focus-visible:border-gado-green/40"
    >
      {/* Header — avatar + comprador + data */}
      <div className="flex items-center gap-2.5 min-w-0">
        <span
          className={`h-10 w-10 shrink-0 rounded-xl grid place-items-center font-cabinet font-bold text-sm ${paletteFor(
            name
          )}`}
        >
          {initials(name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-gado-ink truncate">{name}</p>
          <p className="text-[11px] text-gado-muted truncate">
            {formatDate(tx.date)}{meta && ` · ${meta}`}
          </p>
        </div>
        {tx.status === "em_andamento" && (
          <span className="shrink-0 text-[9px] uppercase tracking-wider font-bold text-gado-gold bg-gado-gold/10 border border-gado-gold/30 rounded-full px-2 py-0.5">
            Em andamento
          </span>
        )}
      </div>

      {/* Stats strip — estilo resumo do PDF */}
      <div className="mt-3 grid grid-cols-3 divide-x divide-gado-line/70 rounded-lg bg-gado-bg/60 py-2">
        <MiniStat label="Cabeças" value={formatNum(tx.total_quantity, 0)} />
        <MiniStat label="Arrobas" value={`${formatNum(tx.total_arrobas, 1)}@`} />
        <MiniStat label="Média/cab" value={`${formatNum(tx.avg_weight, 1)} kg`} />
      </div>

      {/* Total — caixa dourada igual ao TOTAL A PAGAR do PDF */}
      <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-gado-gold/10 border border-gado-gold/20 px-3 py-2">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-gado-ink shrink-0">
          Total a pagar
        </span>
        <span className="font-mono text-sm font-bold text-gado-gold tabular-nums text-right min-w-0 truncate">
          {formatBRL(tx.total_amount)}
        </span>
      </div>
    </button>
  );
}

// ---------- Main ----------

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [detailsTx, setDetailsTx] = useState(null);
  const { data: txs = [], isLoading } = useQuery({
    queryKey: ["transactions"],
    queryFn: async () => (await api.get("/transactions?limit=500")).data,
  });

  const [reportOpen, setReportOpen] = useState(false);
  const now = new Date();
  const [reportMonth, setReportMonth] = useState(String(now.getMonth() + 1));
  const [reportYear, setReportYear] = useState(String(now.getFullYear()));

  const currentYear = now.getFullYear();
  const firstTxYear = txs.length
    ? Math.min(...txs.map((t) => new Date(t.date).getFullYear()))
    : currentYear;
  const yearOptions = [];
  for (let y = currentYear; y >= Math.min(firstTxYear, currentYear); y--) yearOptions.push(y);

  const monthlyReport = () => {
    setReportOpen(false);
    toastWithQuota(downloadMonthlyReport(Number(reportYear), Number(reportMonth)), {
      loading: "Gerando relatório do mês…",
      success: "Relatório baixado",
      error: "Falha ao gerar relatório",
    });
  };

  const thisMonth = txs.filter((t) => {
    const d = new Date(t.date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const sum = (arr, k) => arr.reduce((s, t) => s + (t[k] || 0), 0);

  const monthlyHeads = sum(thisMonth, "total_quantity");
  const monthlyArrobas = sum(thisMonth, "total_arrobas");
  const monthlyValue = sum(thisMonth, "total_amount");

  // Trend: this month vs previous
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonth = txs.filter((t) => {
    const d = new Date(t.date);
    return d.getMonth() === prevMonthDate.getMonth() && d.getFullYear() === prevMonthDate.getFullYear();
  });
  const prevValue = sum(prevMonth, "total_amount");
  const trend = prevValue > 0 ? Math.round(((monthlyValue - prevValue) / prevValue) * 100) : null;

  // last 6 months chart
  const chart = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = txs
      .filter((t) => {
        const td = new Date(t.date);
        return td.getMonth() === d.getMonth() && td.getFullYear() === d.getFullYear();
      })
      .reduce((s, t) => s + (t.total_amount || 0), 0);
    chart.push({ mes: MONTHS[d.getMonth()], valor: Math.round(val) });
  }

  const recent = [...txs]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  const firstName = (user?.name || "").split(" ")[0] || "produtor";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* HERO — dark greeting card with inline stats */}
      <motion.section
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="hero"
        className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-gado-greenDark via-gado-green to-gado-greenDark text-white"
      >
        {/* decorative blur circles */}
        <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-gado-gold/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-16 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />

        <div className="relative px-5 pt-6 pb-5 md:px-8 md:pt-8 md:pb-7">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-gado-goldLight/90 font-semibold">
                {now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
              </p>
              <h1 className="font-cabinet text-3xl md:text-4xl font-extrabold tracking-tight mt-1">
                Olá, {firstName}
                <span className="text-gado-goldLight">.</span>
              </h1>
              <p className="text-sm text-white/70 max-w-md">
                Aqui está o resumo do seu mês. Pronto para pesar mais um lote?
              </p>
            </div>
            <button
              onClick={() => setReportOpen(true)}
              data-testid="monthly-report-button"
              className="self-start inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white/90 hover:text-white px-4 py-2 text-xs font-semibold backdrop-blur transition-colors"
            >
              <FileText size={16} /> Relatório do mês
            </button>
          </div>

          {/* Inline stats — mobile: Comprado takes full width to avoid truncation */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 md:max-w-2xl">
            <StatChip
              label="Cabeças"
              value={formatNum(monthlyHeads, 0)}
              sub={`${thisMonth.length} pesagem${thisMonth.length === 1 ? "" : "s"}`}
            />
            <StatChip
              label="Arrobas"
              value={`${formatNum(monthlyArrobas, 1)}@`}
              sub="Neste mês"
            />
            <StatChip
              label="Comprado"
              value={formatBRL(monthlyValue)}
              sub={
                trend == null
                  ? now.toLocaleDateString("pt-BR", { month: "short" })
                  : trend >= 0
                  ? `+${trend}% vs mês anterior`
                  : `${trend}% vs mês anterior`
              }
              className="col-span-2 md:col-span-1"
            />
          </div>
        </div>
      </motion.section>

      {/* PRIMARY ACTIONS — twin premium cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Nova Pesagem */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
        >
          <Link
            to="/app/nova-pesagem"
            data-testid="dashboard-nova-pesagem"
            className="group relative overflow-hidden rounded-2xl border border-gado-line bg-white p-5 md:p-6 flex items-start gap-4 hover:border-gado-green/40 hover:shadow-lg hover:shadow-gado-green/5 transition-all"
          >
            <div className="h-14 w-14 rounded-2xl bg-gado-green text-white grid place-items-center shrink-0 shadow-lg shadow-gado-green/30 group-hover:scale-105 transition-transform">
              <PlusCircle size={26} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.28em] text-gado-green font-semibold">
                Ação rápida
              </p>
              <h3 className="font-cabinet text-lg md:text-xl font-extrabold text-gado-ink mt-0.5">
                Nova Pesagem
              </h3>
              <p className="text-xs md:text-sm text-gado-muted">
                Registre uma pesagem completa com transporte, parciais e PDF de marca.
              </p>
            </div>
            <ArrowRight
              size={20}
              className="text-gado-green shrink-0 mt-1 group-hover:translate-x-1 transition-transform"
              weight="bold"
            />
          </Link>
        </motion.div>

        {/* Analisar Lote (AI, gold accent) */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
        >
          <Link
            to="/app/analise-video"
            data-testid="cta-analise-video"
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#2D6A4F] via-gado-green to-gado-greenDark text-white p-5 md:p-6 flex items-start gap-4 border border-gado-gold/30 hover:border-gado-gold/60 transition-all"
          >
            <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-gado-gold/20 blur-3xl pointer-events-none group-hover:bg-gado-gold/30 transition-colors" />
            <div className="relative h-14 w-14 rounded-2xl bg-gado-gold text-gado-ink grid place-items-center shrink-0 shadow-lg shadow-gado-gold/30 group-hover:scale-105 transition-transform">
              <VideoCamera size={26} weight="fill" />
            </div>
            <div className="relative flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.28em] text-gado-goldLight font-semibold flex items-center gap-1.5">
                <Sparkle size={11} weight="fill" /> IA em Beta
              </p>
              <h3 className="font-cabinet text-lg md:text-xl font-extrabold text-white mt-0.5">
                Analisar Lote por vídeo
              </h3>
              <p className="text-xs md:text-sm text-white/75">
                Grave um vídeo do lote (até 5 min) e a IA conta os animais e estima o peso do lote.
              </p>
            </div>
            <ArrowRight
              size={20}
              className="relative text-gado-goldLight shrink-0 mt-1 group-hover:translate-x-1 transition-transform"
              weight="bold"
            />
          </Link>
        </motion.div>
      </section>

      {/* RECENT + CHART — 2 col on desktop, stacked on mobile */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Recent pesagens — HIGHLIGHTED */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.12 }}
          className="lg:col-span-3 rounded-2xl border border-gado-line bg-white p-5 md:p-6"
          data-testid="recent-section"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg bg-gado-green/10 grid place-items-center">
                <ClockCounterClockwise size={16} className="text-gado-green" weight="fill" />
              </span>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-gado-muted font-semibold">
                  Últimas pesagens
                </p>
                <h3 className="font-cabinet font-bold text-gado-ink text-sm">Pesagens recentes</h3>
              </div>
            </div>
            <Link
              to="/app/historico"
              data-testid="see-all-historico"
              className="text-xs font-semibold text-gado-green hover:text-gado-greenHover inline-flex items-center gap-1"
            >
              Ver tudo <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {isLoading && <p className="text-sm text-gado-muted py-6 text-center sm:col-span-2">Carregando…</p>}
            {!isLoading && recent.length === 0 && (
              <div className="text-center py-8 sm:col-span-2">
                <Cow size={36} className="mx-auto text-gado-line mb-2" />
                <p className="text-sm text-gado-muted">Nenhuma pesagem ainda.</p>
                <Link
                  to="/app/nova-pesagem"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-gado-green hover:underline"
                >
                  Registrar a primeira <ArrowRight size={12} />
                </Link>
              </div>
            )}
            {recent.map((t) => (
              <RecentRow
                key={t.transaction_id}
                tx={t}
                onOpen={() => setDetailsTx(t)}
              />
            ))}
          </div>
        </motion.div>

        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.18 }}
          className="lg:col-span-2 rounded-2xl border border-gado-line bg-white p-5 md:p-6"
          data-testid="chart-section"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="h-8 w-8 rounded-lg bg-gado-gold/15 grid place-items-center">
              <TrendUp size={16} className="text-gado-gold" weight="fill" />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-gado-muted font-semibold">
                Últimos 6 meses
              </p>
              <h3 className="font-cabinet font-bold text-gado-ink text-sm">Valor comprado</h3>
            </div>
          </div>
          <div className="h-48 md:h-56 min-w-[1px]">
            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
              <BarChart data={chart} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDEFEA" />
                <XAxis
                  dataKey="mes"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: "#5C6A60" }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: "#5C6A60" }}
                  tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                />
                <Tooltip
                  cursor={{ fill: "#F3F5F1" }}
                  formatter={(v) => [formatBRL(v), "Valor"]}
                  contentStyle={{ borderRadius: 10, border: "1px solid #E8EAE6", fontSize: 12 }}
                />
                <Bar dataKey="valor" fill="#1A472A" radius={[6, 6, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </section>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="monthly-report-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-cabinet">Resumo do mês em PDF</DialogTitle>
            <DialogDescription>
              Escolha o período para gerar o PDF consolidado com todas as pesagens.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-[0.16em] font-semibold text-gado-muted">
                Mês
              </label>
              <Select value={reportMonth} onValueChange={setReportMonth}>
                <SelectTrigger data-testid="monthly-report-month-select">
                  <SelectValue placeholder="Mês" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS_FULL.map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-[0.16em] font-semibold text-gado-muted">
                Ano
              </label>
              <Select value={reportYear} onValueChange={setReportYear}>
                <SelectTrigger data-testid="monthly-report-year-select">
                  <SelectValue placeholder="Ano" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={monthlyReport}
              data-testid="monthly-report-generate-button"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gado-green hover:bg-gado-greenDark text-white px-4 py-2.5 text-sm font-semibold transition-colors"
            >
              <FileText size={16} /> Gerar PDF
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TransactionDetailsDialog
        tx={detailsTx}
        open={!!detailsTx}
        onClose={() => setDetailsTx(null)}
        onEdit={() => {
          const id = detailsTx?.transaction_id;
          setDetailsTx(null);
          if (id) navigate(`/app/nova-pesagem?id=${id}`);
        }}
      />
    </div>
  );
}
